import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/format.js';
import { Button } from './Button.jsx';

/**
 * Dialogs and slide-overs. One implementation of focus trapping, Escape, scroll
 * locking, and focus restoration — the things that are easy to get wrong once
 * and then wrong in fifteen places.
 */

const portalTarget = () => (typeof document === 'undefined' ? null : document.body);

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

const useDismissable = (open, onClose) => {
  const panelRef = useRef(null);
  const returnFocus = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    returnFocus.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter(
        (element) => element.getAttribute('tabindex') !== '-1',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    const timer = setTimeout(() => panelRef.current?.focus(), 20);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
      returnFocus.current?.focus?.();
    };
  }, [open, onClose]);

  return panelRef;
};

const CloseButton = ({ onClose }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label="Close"
    className="-mt-1 -mr-1.5 grid size-7 place-items-center rounded text-muted transition-colors hover:bg-mist/60 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal"
  >
    <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
      <path d="M4.3 3.24 8 6.94l3.7-3.7 1.06 1.06-3.7 3.7 3.7 3.7-1.06 1.06-3.7-3.7-3.7 3.7-1.06-1.06 3.7-3.7-3.7-3.7z" />
    </svg>
  </button>
);

const WIDTHS = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-5xl' };

export const Modal = ({ open, onClose, title, description, size = 'md', children, footer }) => {
  const panelRef = useDismissable(open, onClose);
  const target = portalTarget();

  if (!open || !target) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto p-4" role="presentation">
      <div className="fixed inset-0 bg-ink/40" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative my-8 w-full border border-mist bg-surface shadow-[0_20px_60px_-20px_rgba(20,23,26,0.35)] outline-none',
          WIDTHS[size],
        )}
      >
        {title && (
          <header className="flex items-start justify-between gap-4 border-b border-mist px-5 py-3.5">
            <div>
              <h2 className="text-sm font-medium text-ink">{title}</h2>
              {description && <p className="mt-0.5 text-2xs text-muted">{description}</p>}
            </div>
            <CloseButton onClose={onClose} />
          </header>
        )}

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-mist bg-paper px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    target,
  );
};

/** A right-hand panel for editing a row without losing the grid behind it. */
export const Drawer = ({ open, onClose, title, description, children, footer, width = 'max-w-2xl' }) => {
  const panelRef = useDismissable(open, onClose);
  const target = portalTarget();

  if (!open || !target) return null;

  return createPortal(
    <div className="fixed inset-0 z-50" role="presentation">
      <div className="absolute inset-0 bg-ink/35" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'absolute inset-y-0 right-0 flex w-full flex-col border-l border-mist bg-surface outline-none',
          'shadow-[0_0_60px_-15px_rgba(20,23,26,0.3)]',
          width,
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-mist px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-medium text-ink">{title}</h2>
            {description && <p className="mt-0.5 text-2xs text-muted">{description}</p>}
          </div>
          <CloseButton onClose={onClose} />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-mist bg-paper px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    target,
  );
};

/**
 * The confirmation dialog every destructive action routes through. Driven by the
 * UI store so a service can ask for confirmation without a component owning the
 * state — and so the wording is a real sentence rather than the browser's.
 */
export const ConfirmDialog = ({ confirmation }) => {
  if (!confirmation) return null;

  const {
    title = 'Are you sure?',
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'danger',
    resolve,
  } = confirmation;

  return (
    <Modal
      open
      onClose={() => resolve(false)}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={() => resolve(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="sm"
            onClick={() => resolve(true)}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-xs leading-relaxed text-muted">{description}</p>
    </Modal>
  );
};

export default Modal;
